import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import {
  fetchSubcurators,
  fetchCuratorsForDropdown,
  createGuestCurator,
  updateGuestCurator,
  fetchCuratorsForManagement,
  IActiveCuratorWithArchivedCount,
  fetchCuratorMedia,
  ICuratorMedia,
} from "@apis/curatorApis";
import { QUERY_KEYS } from "./queryKeys";
import { CreateGuestCurator, IGuestCuratorRecord } from "@src/types/curator";
import { RoleType } from "@src/types/user";
import { AxiosResponse } from "axios";
import { ApiResponse, SortingParams } from "@src/types/httpApi";

// Fetch subcurators query
export const useSubcuratorsQuery = (
  parentCuratorId: string,
  searchText?: string,
  roleType?: RoleType,
  // Partial: the caller only ever overrides pieces of the config below — `queryKey`
  // and `queryFn` are set here, so requiring them again would make `{ enabled }`
  // impossible to pass.
  options?: Partial<
    UseQueryOptions<AxiosResponse<ApiResponse<IGuestCuratorRecord[]>>, Error>
  >
): UseQueryResult<AxiosResponse<ApiResponse<IGuestCuratorRecord[]>>, Error> => {
  return useQuery<AxiosResponse<ApiResponse<IGuestCuratorRecord[]>>, Error>({
    queryKey: [QUERY_KEYS.SUBCURATORS, parentCuratorId, searchText, roleType],
    queryFn: () => fetchSubcurators(parentCuratorId, searchText, roleType),
    enabled:
      (parentCuratorId !== undefined && parentCuratorId !== "") ||
      roleType === "admin" ||
      roleType === "manager",
    ...options,
  });
};

// Fetch curators for dropdown query
export const useCuratorsForDropdownQuery = (
  parentCuratorId: number,
  options?: UseQueryOptions
): UseQueryResult => {
  return useQuery({
    queryKey: [QUERY_KEYS.CURATORS_FOR_DROPDOWN, parentCuratorId],
    queryFn: () => fetchCuratorsForDropdown(parentCuratorId),
    ...options,
  });
};

// Create guest curator mutation
export const useCreateGuestCuratorMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any, any>,
    Error,
    CreateGuestCurator & { parentCurator: string },
    unknown
  >
): UseMutationResult<
  AxiosResponse<any, any>,
  Error,
  CreateGuestCurator & { parentCurator: string },
  unknown
> => {
  return useMutation<
    AxiosResponse<any, any>,
    Error,
    CreateGuestCurator & { parentCurator: string }
  >({
    mutationFn: (data: CreateGuestCurator & { parentCurator: string }) =>
      createGuestCurator(data),
    ...options,
  });
};

// Update guest curator mutation
export const useUpdateGuestCuratorMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any, any>,
    Error,
    CreateGuestCurator,
    unknown
  >
): UseMutationResult<
  AxiosResponse<any, any>,
  Error,
  CreateGuestCurator,
  unknown
> => {
  return useMutation<
    AxiosResponse<any, any>,
    Error,
    CreateGuestCurator,
    unknown
  >({
    mutationFn: (data: CreateGuestCurator) => updateGuestCurator(data),
    ...options,
  });
};

// Fetch active curators with archived playlist count (infinite query)
// Uses /api/curators/management endpoint which includes archivedPlaylistCount
export const useInfiniteActiveCuratorsQuery = (
  searchText?: string,
  pageSize?: number,
  sorting?: SortingParams,
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.ACTIVE_CURATORS, searchText, pageSize, sorting],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchCuratorsForManagement(searchText, pageParam, pageSize, sorting),
    getNextPageParam: (
      lastPage:
        | AxiosResponse<ApiResponse<IActiveCuratorWithArchivedCount[]>>
        | undefined
    ): number | undefined => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    gcTime: 5000,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// Fetch active curators with archived playlist count (regular query)
// Uses /api/curators/management endpoint which includes archivedPlaylistCount
export const useActiveCuratorsWithArchivedCountQuery = (
  searchText?: string,
  options?: UseQueryOptions<
    AxiosResponse<ApiResponse<IActiveCuratorWithArchivedCount[]>>,
    Error
  >
): UseQueryResult<
  AxiosResponse<ApiResponse<IActiveCuratorWithArchivedCount[]>>,
  Error
> => {
  return useQuery<
    AxiosResponse<ApiResponse<IActiveCuratorWithArchivedCount[]>>,
    Error
  >({
    queryKey: [QUERY_KEYS.ACTIVE_CURATORS, searchText],
    queryFn: () => fetchCuratorsForManagement(searchText),
    ...options,
  });
};

// Fetch curator media images query
export const useCuratorMedia = (
  curatorId: string,
  options?: Omit<
    UseQueryOptions<
      AxiosResponse<ApiResponse<ICuratorMedia[]>>,
      Error,
      AxiosResponse<ApiResponse<ICuratorMedia[]>>,
      any
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<AxiosResponse<ApiResponse<ICuratorMedia[]>>, Error> => {
  return useQuery<AxiosResponse<ApiResponse<ICuratorMedia[]>>, Error>({
    queryKey: [QUERY_KEYS.GET_CURATOR_MEDIA, curatorId],
    queryFn: () => fetchCuratorMedia(curatorId),
    enabled: curatorId !== undefined && curatorId !== "",
    ...options,
  } as any);
};
